<script>
  // @ts-check
  import { getContext } from "svelte";
  import { v4 as uuid } from "@lukeed/uuid";
  import Link from "../../atoms/Link.svelte";
  import Image from "../../atoms/Image.svelte";
  import { getTheme } from "../../utilities/themes";
  import { VALID_LAYOUTS } from "../CardLayout.svelte";

  /** Primarily used for testing
   * @type {string} */
  export let id = undefined;

  /**
   * The featured image in the card
   * @type {import('../../types/shared.js').Image} Image
   */
  export let featuredImage = undefined;

  /**
   * The title of the component
   * @type {string}
   */
  export let title;

  /**
   * The optional description body of the component
   * @type {string}
   */
  export let description = null;

  /**
   * The call to action for the component
   * @type {import('../../types/shared.js').CTA} CTA
   */
  export let cta = undefined;

  /**
   * The type in the card
   * @type {boolean}
   */
  export let isFeatured = false;

  /**
   * The theme of the hero section
   * @type {import('../../utilities/themes.js').ThemeName} ThemeName
   */
  export let theme = "skyBlue";

  let currentTheme = theme !== "white" ? getTheme(theme) : getTheme("skyBlue");
  let isCardFocused = false;
  let isLinkHovered = false;
  let linkId = cta.id ?? `link_card_${uuid()}`;

  const hasImage = !!featuredImage?.src;

  // Layout-specific Overrides
  const layoutContext = getContext("CardLayout");
  $: isTwoColumns = layoutContext?.layout === VALID_LAYOUTS.TwoColumns;
  const centeredLayout = getContext("CenteredLayout");
  $: aspectRatioClasses =
    isFeatured || centeredLayout || !isTwoColumns ? "aspect-1/1" : "aspect-3/2";
  $: focusClass = isCardFocused
    ? "outline-2 outline-dashed outline-offset-[10px] outline-black"
    : "";
  $: imageScaleClass = isLinkHovered ? "scale-[103%]" : "";

  let titleClass = "heading-sm";
  if (isFeatured) {
    titleClass = "heading-md";
  } else if (centeredLayout) {
    titleClass = "heading-xs";
  }
  const titlePaddingClass = hasImage && !centeredLayout ? "pt-6 md:pt-7 lg:pt-7.5 xl:pt-8" : "";
  const contentClass = centeredLayout ? "py-2 lg:pt-7.5 lg:pb-0 xl:pt-8" : "grow";
  const containerLayoutClass = centeredLayout
    ? "gap-x-2 *:basis-1/2 md:gap-x-3 lg:flex-col"
    : "flex-col";
  const bodyClass = isFeatured ? "body-md" : "body-sm";
  const linkPaddingClass = hasImage ? "pt-15 lg:pt-20" : "pt-7.5 lg:pt-10";
  let containerHeightClass =
    "h-full min-h-[440px] md:min-h-[448px] lg:min-h-[368px] xl:min-h-[528px]";

  let containerPaddingClasses = "p-0";

  if (theme !== "white") {
    if (hasImage) {
      containerPaddingClasses = "p-2 md:p-3 xl:p-4";
    } else if (layoutContext?.layout === VALID_LAYOUTS.TwoColumns) {
      containerPaddingClasses = "p-8 md:p-10 lg:p-12.5 xl:p-15";
    } else if (centeredLayout) {
      containerPaddingClasses = "p-3 xl:p-4";
    } else {
      containerPaddingClasses = "p-4 md:p-5 lg:p-6 xl:p-8";
    }
  }

  if (layoutContext?.layout === VALID_LAYOUTS.TwoColumns) {
    containerHeightClass = hasImage
      ? "h-full min-h-[368px] md:min-h-[385px] lg:min-h-[609px] xl:min-h-[795px]"
      : "h-full min-h-[330px] md:min-h-[336px] lg:min-h-[624px] xl:min-h-[864px]";
  } else if (layoutContext?.layout === VALID_LAYOUTS.ThreeColumns) {
    containerHeightClass = hasImage
      ? "h-full min-h-[544px] md:min-h-[562px] lg:min-h-[665px] xl:min-h-[870px]"
      : "h-full min-h-[440px] md:min-h-[448px] lg:min-h-[523px] xl:min-h-[736px]";
  } else if (centeredLayout) {
    containerHeightClass = "";
  }
</script>

{#if cta && cta.url && title}
  <div
    {id}
    class="{theme !== 'white'
      ? currentTheme.backgroundColor
      : 'bg-transparent'} {currentTheme.textColor} {focusClass} {containerPaddingClasses} {containerHeightClass} relative flex w-full {containerLayoutClass}"
  >
    {#if hasImage}
      <Image
        {...featuredImage}
        {aspectRatioClasses}
        containerClass="overflow-hidden"
        elementClass="w-full h-auto transition-all duration-[400ms] ease-in-out {imageScaleClass}"
      />
    {/if}
    <div class="flex flex-col {contentClass}">
      <p class="{titleClass} {titlePaddingClass}">
        <a
          id={linkId}
          href={cta.url}
          aria-describedby={linkId}
          class="no-underline outline-none after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:z-10 focus-visible:outline-none"
          data-sveltekit-reload
          on:focusin={() => (isCardFocused = true)}
          on:focusout={() => (isCardFocused = false)}
          on:mouseenter={() => (isLinkHovered = true)}
          on:mouseleave={() => (isLinkHovered = false)}
        >
          {title}
        </a>
      </p>
      {#if description && !centeredLayout}
        <p class="{bodyClass} pt-3 lg:pt-4">
          {description}
        </p>
      {/if}
      {#if cta.text}
        <Link
          id={linkId}
          class="{linkPaddingClass} mt-auto"
          ariaHidden={true}
          label={cta.text}
          variant="small"
          {theme}
          {isLinkHovered}
        />
      {/if}
    </div>
  </div>
{/if}
